/**
 * ManagedDevicesSection
 * Reusable collapsible section that displays a list of managed/controlled devices
 * with name, type, location, attributes, and optional navigation to the unit.
 * When controlGroupId (and propertyId) are provided, the section edit action opens the control group editor.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import BorderedSection from '../../shared/borderedSection';
import EditIcon from '@mui/icons-material/Edit';
import { ControlGroupEditorDialog } from '../../controlGroups/control-group-editor-dialog';
import { useAuthenticator } from '../../../auth/AuthenticationContext';
import { displayUnits } from './display-value';

/** Expected shape of each device entry in the data object */
export interface ManagedDeviceEntry {
  name?: string;
  type?: string;
  floorName?: string | null;
  unitName?: string | null;
  propertyId?: string | null;
  unitId?: string | null;
  isOnline?: boolean;
  attributes?: Record<string, unknown> | null;
}

export interface ManagedDevicesSectionProps {
  /** Data object keyed by index/position; each value is a device entry */
  data: Record<string, unknown> | null | undefined;
  /** Section title */
  title?: string;
  /** Message when data is empty or invalid */
  emptyMessage?: string;
  /** Custom comparator for sorting entries; default sorts by numeric key ascending */
  sortEntries?: (a: [string, unknown], b: [string, unknown]) => number;
  /** Base path for unit links; default builds `/properties/${propertyId}/units/${unitId}` */
  getUnitUrl?: (propertyId: string, unitId: string) => string;
  /** When set with propertyId, the section edit action opens the control group editor. */
  controlGroupId?: string;
  propertyId?: string;
  unitId?: string;
}

const defaultSortEntries = (
  a: [string, unknown],
  b: [string, unknown],
): number => Number(a[0]) - Number(b[0]);

const formatAttrValue = (val: unknown): string => {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
};

export function ManagedDevicesSection({
  data,
  title = 'Managed Devices',
  emptyMessage = 'No device details available.',
  sortEntries = defaultSortEntries,
  getUnitUrl,
  controlGroupId,
  propertyId,
  unitId,
}: ManagedDevicesSectionProps) {
  const navigate = useNavigate();
  const [editorOpen, setEditorOpen] = useState(false);
  const { user } = useAuthenticator();

  const canEditControlGroup =
    !!controlGroupId && !!propertyId && typeof controlGroupId === 'string';
  const handleEditClick = () => {
    if (canEditControlGroup) setEditorOpen(true);
    else alert('hello');
  };

  const resolveUnitUrl = (propertyId: string, unitId: string): string => {
    if (getUnitUrl) return getUnitUrl(propertyId, unitId);
    return `/properties/${propertyId}/units/${unitId}`;
  };

  if (data == null || typeof data !== 'object' || Array.isArray(data)) {
    return (
      <BorderedSection title={title}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </BorderedSection>
    );
  }

  const entries = Object.entries(data)
    .filter(([key, value]) => typeof value !== 'string')
    .sort(sortEntries);

  return (
    <>
      <BorderedSection
        actionable={true}
        clickAction={handleEditClick}
        actionIcon={<EditIcon style={{ fontSize: '20px' }} />}
        title={title}
        labelHeight={'20px'}
      >
        <Stack spacing={2} sx={{ padding: '8px 0' }}>
          {entries.map(([indexKey, device]) => {
            const entry = device as ManagedDeviceEntry | null | undefined;
            if (entry == null || typeof entry !== 'object') return null;

            const name = entry.name ?? 'Unnamed';
            const type = entry.type ?? '—';
            const floorName = entry.floorName ?? null;
            const unitName = entry.unitName ?? null;
            const locationLine =
              [floorName, unitName].filter(Boolean).join(' / ') || '—';
            const propertyId = entry.propertyId ?? null;
            const unitId = entry.unitId ?? null;
            const isOnline = entry.isOnline !== false;
            const unitUrl =
              propertyId && unitId ? resolveUnitUrl(propertyId, unitId) : null;
            const attrs = entry.attributes;
            const attrEntries =
              attrs && typeof attrs === 'object' && !Array.isArray(attrs)
                ? Object.entries(attrs)
                : [];

            return (
              <Box
                key={indexKey}
                sx={{
                  border: '2px solid',
                  borderColor: isOnline ? 'divider' : 'error.main',
                  borderRadius: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'stretch',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ px: 1.5, py: 1, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" component="span">
                      {name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="span"
                      sx={{ ml: 1 }}
                    >
                      {type}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                    >
                      {locationLine}
                    </Typography>
                  </Box>
                  {attrEntries.length > 0 ? (
                    <Box
                      sx={{
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        pt: 0.5,
                        px: 1,
                        pb: 0.5,
                      }}
                    >
                      <TableContainer sx={{ minHeight: 0 }}>
                        <Table
                          size="small"
                          aria-label={`Attributes for ${name}`}
                          sx={{
                            '& .MuiTableCell-root': {
                              py: 0.25,
                              px: 0.75,
                            },
                          }}
                        >
                          <TableBody>
                            {attrEntries.map(([key, valueArray]) => {
                              const [value, units] = valueArray as Array<any>;
                              return (
                                <TableRow key={key}>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    sx={{
                                      fontWeight: 500,
                                      width: '40%',
                                      borderBottom: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                  >
                                    {key}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      borderBottom: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                  >
                                    {formatAttrValue(value)}
                                    {displayUnits(units, user)}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ) : null}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 0.5,
                    px: 1,
                    py: 1,
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                  }}
                >
                  {unitUrl ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => navigate(unitUrl)}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      View unit
                    </Button>
                  ) : null}
                  {!isOnline ? (
                    <Typography
                      variant="caption"
                      sx={{ color: 'error.main', fontWeight: 600 }}
                    >
                      Offline
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </BorderedSection>
      {canEditControlGroup && (
        <ControlGroupEditorDialog
          open={editorOpen}
          controlGroupId={controlGroupId!}
          propertyId={propertyId!}
          unitId={unitId ?? ''}
          onClose={() => setEditorOpen(false)}
        />
      )}
    </>
  );
}

export const managedDevicesRenderer = (
  data: Record<string, unknown> | null | undefined,
) => <ManagedDevicesSection data={data} />;
